/**
 * G4 — with-q + entity=all Vše anatomy: Produkty | Obchody | Tenanti with counts.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../shared/ui/Card.js';
import { DIRECTORY_EMPTY_PANEL_MAX_CLASS } from '../../shared/ui/directoryPickerLayout.js';
import { BrowseShopDirectoryResults } from './BrowseShopDirectoryResults.js';
import { BrowseTenantDirectoryResults } from './BrowseTenantDirectoryResults.js';
import type { PublicCustomerSalesPointDTO } from '../shops/publicSalesPointsApi.js';
import type { PublicCustomerTenantDTO } from '../tenant-select/publicTenantApi.js';

export type BrowseVseSearchSectionsProps = {
  readonly productCount: number;
  readonly productsSection: ReactNode;
  readonly shops: readonly PublicCustomerSalesPointDTO[];
  readonly shopsPending: boolean;
  readonly shopsError: boolean;
  readonly onShopsRetry: () => void;
  /** When omitted, Tenanti section fetches public customer-tenants itself. */
  readonly tenants?: readonly PublicCustomerTenantDTO[];
  readonly tenantsPending?: boolean;
  readonly tenantsError?: boolean;
  readonly onTenantsRetry?: () => void;
  readonly query: string;
};

export function BrowseVseSearchSections(
  props: BrowseVseSearchSectionsProps,
): JSX.Element {
  const {
    productCount,
    productsSection,
    shops,
    shopsPending,
    shopsError,
    onShopsRetry,
    tenants,
    tenantsPending = false,
    tenantsError = false,
    onTenantsRetry,
    query,
  } = props;
  const { t } = useTranslation('customer');
  const shopCount = shops.length;
  const tenantCount =
    tenants === undefined
      ? 0
      : tenants.filter((tenant) => {
          const q = query.trim().toLocaleLowerCase();
          if (q.length === 0) {
            return true;
          }
          return (
            tenant.name.toLocaleLowerCase().includes(q) ||
            tenant.code.toLocaleLowerCase().includes(q)
          );
        }).length;

  const allEmpty =
    !shopsPending &&
    !tenantsPending &&
    productCount === 0 &&
    shopCount === 0 &&
    (tenants === undefined ? false : tenantCount === 0);

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="customer-browse-vse-sections"
      data-product-count={String(productCount)}
      data-shop-count={String(shopCount)}
      data-tenant-count={String(tenantCount)}
    >
      <section
        className="space-y-3"
        data-testid="customer-browse-vse-products"
        aria-labelledby="customer-browse-vse-products-heading"
      >
        <h2
          id="customer-browse-vse-products-heading"
          className="text-base font-semibold text-[var(--color-on-surface)]"
          data-testid="customer-browse-vse-products-heading"
        >
          {t('browse.discovery.vseProductsHeading', {
            count: productCount,
            defaultValue: `Produkty (${productCount})`,
          })}
        </h2>
        {productCount > 0 ? (
          productsSection
        ) : (
          <p className="text-sm text-[var(--color-on-surface-muted)]">
            {t('browse.discovery.vseProductsEmpty', {
              defaultValue: 'Žádné produkty',
            })}
          </p>
        )}
      </section>

      <section
        className="space-y-3"
        data-testid="customer-browse-vse-shops"
        aria-labelledby="customer-browse-vse-shops-heading"
      >
        <h2
          id="customer-browse-vse-shops-heading"
          className="text-base font-semibold text-[var(--color-on-surface)]"
          data-testid="customer-browse-vse-shops-heading"
        >
          {t('browse.discovery.vseShopsHeading', {
            count: shopCount,
            defaultValue: `Obchody (${shopCount})`,
          })}
        </h2>
        <BrowseShopDirectoryResults
          items={shops}
          isPending={shopsPending}
          isError={shopsError}
          onRetry={onShopsRetry}
          showHeading={false}
          testId="customer-browse-vse-shop-directory"
        />
      </section>

      <section
        className="space-y-3"
        data-testid="customer-browse-vse-tenants"
        aria-labelledby="customer-browse-vse-tenants-heading"
      >
        <h2
          id="customer-browse-vse-tenants-heading"
          className="text-base font-semibold text-[var(--color-on-surface)]"
          data-testid="customer-browse-vse-tenants-heading"
        >
          {t('browse.discovery.vseTenantsHeading', {
            count: tenantCount,
            defaultValue:
              tenants === undefined
                ? 'Business partners'
                : `Business partners (${tenantCount})`,
          })}
        </h2>
        <BrowseTenantDirectoryResults
          query={query}
          showHeading={false}
          {...(tenants !== undefined
            ? {
                items: tenants,
                isPending: tenantsPending,
                isError: tenantsError,
                onRetry: onTenantsRetry,
              }
            : {})}
        />
      </section>

      {allEmpty ? (
        <Card padded className={DIRECTORY_EMPTY_PANEL_MAX_CLASS}>
          <p
            className="text-sm text-[var(--color-on-surface-muted)]"
            data-testid="customer-browse-vse-all-empty"
          >
            {t('browse.discovery.emptyBody')}
          </p>
        </Card>
      ) : null}
    </div>
  );
}
